import React, { useState, useContext, useEffect } from 'react';
import './BookingForm.css';
import './MainContainer.css'; // Ensure this import is present for shared width/height
import Header from './Header';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import {authHeader} from "./helpers/auth-header";
import {authService} from "./services/authentication-service";
import { BS_BASE } from "./configs/api";


function BookingForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedDate, selectedTime, isSchool, soleUse, otherUsers, isSingleLesson, clientId } = location.state || {};
  const { users } = location.state || {}; // users passed from CalendarDay (array or undefined)

  // Map special quick-select values to labels
  const selectedTimeLabel = React.useMemo(() => {
    if (selectedTime === -1) return "AM";
    if (selectedTime === -2) return "PM";
    if (selectedTime === -3) return "Day";
    return selectedTime;
  }, [selectedTime]);

  const auth = useContext(AuthContext);

  // Lesson vs School terms
  const termsLesson = [
    'Bookings must still be paid for if cancelled within 24 hours of the lesson.',
    'Please arrive on time; missed time cannot be made up.',
    'Lessons may be rescheduled up to 24 hours in advance, subject to availability.',
    'Bookings must be accepted before they are confirmed.',
    'You will receive an email once your booking is accepted.',
    'By submitting this form, you agree to these terms.'
  ];
  const termsSchool = [
    'Sole Use is subject to availability and may be declined at busy times.',
    'Please arrive on time; missed time cannot be made up.',
    'The yard reserves the right to cancel bookings at any time.',
    'The yard is not responsible for any injuries and riders ride at their own risk.',
    'Riders must wear appropriate safety gear at all times.',
    'Please report any accidents or injuries to yard staff immediately.',
    'By submitting this form, you agree to the school booking terms.'
  ];
  const terms = isSchool ? termsSchool : termsLesson;

  // Remove local selectedTime state, use prop instead
  const handleSubmit = (e) => {
    e.preventDefault();
    const formattedDate = selectedDate
      ? `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${selectedDate
          .getDate()
          .toString()
          .padStart(2, '0')}`
      : '';

    const currentUser = authService.currentUserValue;

    const isSpecial = typeof selectedTime === 'number' && selectedTime < 0;
    const startForApi = isSpecial ? "09:00" : selectedTime;
    const specialForApi = isSpecial ? selectedTime : 0;

    // Helper: compute end time as start + 45 minutes (HH:mm)
    const computeEndTime = (start) => {
      if (!start || typeof start !== 'string' || !/^\d{2}:\d{2}$/.test(start)) return null;
      const [hh, mm] = start.split(":" ).map(n => parseInt(n, 10) || 0);
      const total = hh * 60 + mm + 45;
      const endH = Math.floor(total / 60);
      const endM = total % 60;
      const normH = ((endH % 24) + 24) % 24; // keep in 0-23
      return `${String(normH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
    };

    // Single-lesson: call new book-single-lesson endpoint
    if (isSingleLesson) {
      const userId = clientId; // always use selected client from dropdown
      const payload = {
        userId,
        instructorId: 1,
        date: formattedDate,
        day: selectedDate
          ? selectedDate.toLocaleDateString(undefined, { weekday: 'long' })
          : '',
        startTime: startForApi,
        endTime: computeEndTime(startForApi),
        type: isSchool ? 1 : 0,
      };

      fetch(`${BS_BASE}/book-single-lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        },
        body: JSON.stringify(payload)
      })
        .then(res => {
          if (!res.ok) throw new Error('Booking failed');
          return res.json().catch(() => null);
        })
        .then(data => {
          alert('Single lesson booked');
          navigate('/admin-dashboard', { state: { booking: data } });
        })
        .catch(err => {
          alert('Error booking single lesson: ' + err.message);
        });

      return;
    }

    // Existing behaviour: normal lesson/school booking
    const payload = {
      date: formattedDate,
      startTime: startForApi,
      userId: currentUser ? currentUser.id : null,
      isSchool: isSchool || false,
      soleUse: soleUse || false,
      special: specialForApi
    };

    fetch(`${BS_BASE}/book-slot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) throw new Error('Booking failed');
        return res.json();
      })
      .then(data => {
        alert('Booking successful');
        navigate('/admin-dashboard', { state: { booking: data } });
      })
      .catch(err => {
        alert('Error booking slot: ' + err.message);
      });
  };

  return (
    <div className="bookingform-page">
      <form className="bookingform-form main-container" onSubmit={handleSubmit} style={{ position: 'relative' }}>
        <Header onBack={() => navigate(-1)} />
        <div style={{ marginTop: "10px", marginBottom: "18px" }}>
          <h2 style={{ marginBottom: 0 }}>
            Book {isSchool ? 'School' : 'Lesson'}
            <br />
            {selectedDate && (
              <span
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 900,
                  display: "block",
                  marginTop: "2px",
                  color: "#4fa3c7"
                }}
              >
                {selectedDate.toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
            {selectedTime != null && selectedTime !== "" && (
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: 700,
                  marginTop: 0,
                  color: "#4fa3c7"
                }}
              >
                {selectedTimeLabel}
                <br />
                {users && users.length > 0 && (
                  <div style={{ fontSize: "50%", lineHeight: 1.2 }}>
                    <strong>Other Riders:</strong>
                    <div style={{ margin: 0 }}>
                      {users.map((user, index) => (
                        <div key={index}>{user}</div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </h2>
        </div>

        {/* Removed First Name, Last Name, and Email fields */}
        {/*
        <label>
          First Name:
          <input
            type="text"
            value={name}
            required
            onChange={e => setFirstName(e.target.value)}
          />
        </label>
        <label>
          Last Name:
          <input
            type="text"
            value={lastName}
            required
            onChange={e => setLastName(e.target.value)}
          />
        </label>
        <label>
          Email:
          <input
            type="email"
            value={email}
            required
            onChange={e => setEmail(e.target.value)}
          />
        </label>
        */}

        <div style={{
          fontSize: "0.85rem",
          color: "#666",
          margin: "0px 0 2px 0",
          lineHeight: 1.4,
          background: "#f4f4f4",
          padding: "10px 14px",
          borderRadius: "8px"
        }}>
          <strong style={{ fontSize: "0.90rem" }}>
            {isSchool ? 'School Terms and Conditions' : 'Terms and Conditions'}
          </strong>
          <ul style={{ fontSize: "0.70rem", margin: "2px 0 0 5px", padding: 0 }}>
            {terms.map((t, i) => (<li key={i}>{t}</li>))}
          </ul>
        </div>

        <button
          className="bookingform-button"
          type="submit"
          disabled={!selectedTime}
          style={{
            fontSize: "1.3rem",
            padding: "10px 38px",
            borderRadius: "10px",
            marginTop: "18px",
            fontWeight: 700,
            background: "#4fa3c7",
            color: "#fff",
            border: "none",
            cursor: !selectedTime ? "not-allowed" : "pointer",
            opacity: !selectedTime ? 0.6 : 1,
            transition: "background 0.2s"
          }}
        >
          Submit
        </button>
      </form>
    </div>
  );
}

export default BookingForm;